import React, { useState, useEffect, useCallback } from "react";
import "./AdminMysteryBox.css";
import { getAccessToken } from "../../../utils/authStorage";

const AdminMysteryBox = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({});

  // Backend mounts admin VIP routes at `/api/admin/vip` (not `/api/v1/admin/vip`)
  const API_URL =
    import.meta.env.VITE_API_URL || "https://pixelmoonstore.in/api/v1";
  const BASE_URL = API_URL.includes("/v1")
    ? API_URL.substring(0, API_URL.lastIndexOf("/v1"))
    : API_URL;

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      // Change the API path to match backend route mounting
      const token = getAccessToken();
      const response = await fetch(`${BASE_URL}/admin/vip/mystery-boxes`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch mystery box configs");
      }

      const data = await response.json();
      if (data.success) {
        setConfigs(data.configs);
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to fetch mystery box configs",
      );
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleEdit = (config) => {
    setEditingTier(config.tier);
    setFormData({
      tier: config.tier,
      vipLevel: config.vipLevel,
      name: config.name,
      description: config.description,
      dropChances: { ...config.dropChances },
      rewards: {
        common: { xpRange: { ...config.rewards.common.xpRange } },
        rare: { xpRange: { ...config.rewards.rare.xpRange } },
        epic: { xpRange: { ...config.rewards.epic.xpRange } },
        legendary: { xpRange: { ...config.rewards.legendary.xpRange } },
      },
    });
  };

  const handleSave = async () => {
    try {
      setSaving(editingTier);
      // Change the API path to match backend route mounting
      const token = getAccessToken();
      const response = await fetch(
        `${BASE_URL}/admin/vip/mystery-boxes/${editingTier}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify(formData),
        },
      );

      const data = await response.json();

      if (data.success) {
        await fetchConfigs();
        setEditingTier(null);
        setFormData({});
      } else {
        setError(data.message);
      }
    } catch {
      setError("Failed to save mystery box config");
    } finally {
      setSaving(null);
    }
  };

  const handleCancel = () => {
    setEditingTier(null);
    setFormData({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDropChanceChange = (rarity, value) => {
    setFormData((prev) => ({
      ...prev,
      dropChances: {
        ...prev.dropChances,
        [rarity]: parseFloat(value) || 0,
      },
    }));
  };

  const handleRewardChange = (rarity, field, value) => {
    setFormData((prev) => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [rarity]: {
          ...prev.rewards[rarity],
          [field]: field.includes("Range")
            ? {
                ...prev.rewards[rarity][field],
                [value.type]: parseInt(value.value) || 0,
              }
            : value,
        },
      },
    }));
  };

  const getTierColor = (tier) => {
    const colors = {
      // Handle VIP format
      VIP1: "#CD7F32",
      VIP2: "#C0C0C0",
      VIP3: "#FFD700",
      // Handle Tier format from backend
      "Tier I": "#CD7F32",
      "Tier II": "#C0C0C0",
      "Tier III": "#FFD700",
    };
    return colors[tier] || "#666";
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: "#9CA3AF",
      rare: "#3B82F6",
      epic: "#8B5CF6",
      legendary: "#F59E0B",
    };
    return colors[rarity] || colors.common;
  };

  if (loading) {
    return (
      <div className="admin-mystery-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading Mystery Box Configurations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-mystery-container">
        <div className="error-state">
          <h3>Error</h3>
          <p>{error}</p>
          <button type="button" onClick={fetchConfigs} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-mystery-container">
      <div className="admin-header">
        <h1>🎁 Mystery Box Administration</h1>
        <p>Configure mystery box rewards and drop rates for each VIP tier</p>
      </div>

      <div className="configs-grid">
        {configs.map((config) => (
          <div key={config.tier} className="config-card">
            <div
              className="config-header"
              style={{ borderColor: getTierColor(config.tier) }}
            >
              <h3 style={{ color: getTierColor(config.tier) }}>
                {config.name || `${config.tier} Mystery Box`}
              </h3>
              <div
                className="tier-badge"
                style={{ backgroundColor: getTierColor(config.tier) }}
              >
                {config.tier}
              </div>
            </div>

            {editingTier === config.tier ? (
              <div className="edit-form">
                <div className="form-group">
                  <label htmlFor="mystery-box-name">Box Name:</label>
                  <input
                    id="mystery-box-name"
                    type="text"
                    value={formData.name || ""}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Mystery Box Name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mystery-box-description">Description:</label>
                  <textarea
                    id="mystery-box-description"
                    value={formData.description || ""}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Box Description"
                    rows="3"
                  />
                </div>

                <h4>Drop Chances (%)</h4>
                <div className="drop-chances">
                  {Object.entries(formData.dropChances || {}).map(
                    ([rarity, chance]) => (
                      <div key={rarity} className="chance-input">
                        <label
                          htmlFor={`drop-chance-${rarity}`}
                          style={{ color: getRarityColor(rarity) }}
                        >
                          {rarity.charAt(0).toUpperCase() + rarity.slice(1)}:
                        </label>
                        <input
                          id={`drop-chance-${rarity}`}
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={chance}
                          onChange={(e) =>
                            handleDropChanceChange(rarity, e.target.value)
                          }
                        />
                        <span>%</span>
                      </div>
                    ),
                  )}
                </div>

                <h4>Reward Ranges</h4>
                <div className="rewards-config">
                  {Object.entries(formData.rewards || {}).map(
                    ([rarity, reward]) => (
                      <div key={rarity} className="reward-section">
                        <h5 style={{ color: getRarityColor(rarity) }}>
                          {rarity.charAt(0).toUpperCase() + rarity.slice(1)}{" "}
                          Rewards
                        </h5>

                        <div className="reward-ranges">
                          <div className="range-group">
                            <label htmlFor={`xp-min-${rarity}`}>
                              XP Range:
                            </label>
                            <div className="range-inputs">
                              <input
                                id={`xp-min-${rarity}`}
                                type="number"
                                placeholder="Min"
                                value={reward.xpRange?.min || ""}
                                onChange={(e) =>
                                  handleRewardChange(rarity, "xpRange", {
                                    type: "min",
                                    value: e.target.value,
                                  })
                                }
                              />
                              <span>-</span>
                              <input
                                id={`xp-max-${rarity}`}
                                type="number"
                                placeholder="Max"
                                value={reward.xpRange?.max || ""}
                                onChange={(e) =>
                                  handleRewardChange(rarity, "xpRange", {
                                    type: "max",
                                    value: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="save-button"
                    onClick={handleSave}
                    disabled={saving === config.tier}
                  >
                    {saving === config.tier ? (
                      <>
                        <div className="spinner small"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                    disabled={saving === config.tier}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="config-display">
                <div className="config-info">
                  <p className="description">{config.description}</p>

                  <div className="drop-chances-display">
                    <h4>Drop Chances:</h4>
                    {Object.entries(config.dropChances).map(
                      ([rarity, chance]) => (
                        <div key={rarity} className="chance-display">
                          <span
                            className="rarity-dot"
                            style={{ backgroundColor: getRarityColor(rarity) }}
                          ></span>
                          <span className="rarity-name">{rarity}</span>
                          <span className="chance-value">{chance}%</span>
                        </div>
                      ),
                    )}
                  </div>

                  <div className="rewards-display">
                    <h4>Rewards:</h4>
                    {Object.entries(config.rewards).map(([rarity, reward]) => (
                      <div key={rarity} className="reward-display">
                        <h5 style={{ color: getRarityColor(rarity) }}>
                          {rarity}
                        </h5>
                        <div className="reward-info">
                          <span>
                            XP: {reward.xpRange.min}-{reward.xpRange.max}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  className="edit-button"
                  onClick={() => handleEdit(config)}
                >
                  Edit Configuration
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {configs.length === 0 && (
        <div className="no-configs">
          <h3>No Mystery Box Configurations Found</h3>
          <p>No mystery box configurations are currently set up.</p>
        </div>
      )}
    </div>
  );
};

export default AdminMysteryBox;
